import moment from 'moment'
import refreshContent from './refreshContent'
import { mapState, mapMutations } from 'vuex'
import {
  propertyFullAddress,
  roundOffMinutes,
  getClientFavFromPropertyId
} from '../../utils/propertyUtils'
import {
  isPastTime,
  isCustomStartInRoute,
  isCustomEndInRoute,
  isActiveRouteStop,
  isDeclinedRoute,
  isNotScheduledType,
  isCancelledRoute
} from '../../utils/routeUtils'
import { isEmpty } from '../../utils/generalUtils'
import setRoute from '../../services/directionService'
import { getPropertyById } from '../../services/propertiesService'
import {
  updateRouteDataOnResponse,
  createCalendarTimeFrame
} from '../../services/routeService'

export default {
  mixins: [refreshContent],

  data() {
    return {
      directionServiceRoute: null,
      startLocationAddress: null,
      startLocationTime: null,
      endLocationAddress: null,
      endLocationTime: null
    }
  },

  computed: {
    ...mapState('agent', [
      'agentRouteData',
      'routeEditMode',
      'routeCalculatedArray',
      'selectedStartLocation',
      'selectedEndLocation'
    ]),
    ...mapState('common', ['profileStatus']),
    ...mapState('client', ['clientFavorites', 'clientRouteData'])
  },

  methods: {
    ...mapMutations('agent', {
      setAgentRouteData: 'SET_AGENT_ROUTE_DATA',
      setSelectedStartLocation: 'SET_SELECTED_START_LOCATION',
      setSelectedEndLocation: 'SET_SELECTED_END_LOCATION',
      setRouteEditMode: 'SET_ROUTE_EDIT_MODE',
      setRouteCalculatedArray: 'SET_ROUTE_CALCULATED_ARRAY',
      setRht: 'SET_RHT'
    }),
    ...mapMutations('client', {
      setClientData: 'SET_CLIENT_DATA',
      setClientRouteData: 'SET_CLIENT_ROUTE_DATA'
    }),
    ...mapMutations('common', {
      setSelectedRoute: 'SET_SELECTED_ROUTE'
    }),

    isPastRoute(routeData) {
      return !isEmpty(routeData) && isPastTime(routeData.startTime)
    },

    async getRouteDirections(
      customStops,
      routeData,
      showOnlyScheduled = false
    ) {
      customStops = customStops.filter(stop => stop.arrivalTime !== null)
      let updatedRoute = JSON.parse(JSON.stringify(routeData))
      let updatedCustomStops = [...customStops]
      let source = null
      let firstWaypointIndex = 0
      let destination = null

      if (isCustomStartInRoute(routeData) || isCancelledRoute(routeData)) {
        source = `${routeData.startLocation[0]}, ${routeData.startLocation[1]}`
      } else {
        source = `${customStops[0].latitude}, ${customStops[0].longitude}`
        firstWaypointIndex = 1
      }

      if (
        customStops.length > 1 ||
        isCustomStartInRoute(routeData) ||
        isCustomEndInRoute(routeData)
      ) {
        let thisTime = routeData.startTime
        let waypoints = []
        for (let i = firstWaypointIndex; i < customStops.length; i++) {
          const stop = customStops[i]
          if (stop.propertyId) {
            if (showOnlyScheduled) {
              if (isActiveRouteStop(stop)) {
                waypoints.push({
                  location: `${stop.latitude}, ${stop.longitude}`,
                  stopover: true
                })
                thisTime = stop.arrivalTime
              }
            } else {
              waypoints.push({
                location: `${stop.latitude}, ${stop.longitude}`,
                stopover: true
              })
              thisTime = stop.arrivalTime
            }
          }
        }

        const lastIndex = customStops.length - 1
        if (isCustomEndInRoute(routeData)) {
          destination = `${routeData.endLocation[0]}, ${routeData.endLocation[1]}`
        } else {
          destination = `${customStops[lastIndex].latitude}, ${customStops[lastIndex].longitude}`
          waypoints.pop()
        }

        const returnedRoute = await setRoute(source, destination, waypoints)

        if (returnedRoute && returnedRoute.status === 'OK') {
          updatedRoute.returnedRoute = { ...returnedRoute }
          if (isCustomStartInRoute(routeData)) {
            const arrivalTime = moment(routeData.startTime).utc()
            const locationAddress =
              returnedRoute.routes[0].legs[0].start_address
            updatedCustomStops.unshift({
              Address: `Custom Start Location: ${locationAddress}`,
              arrivalTime: roundOffMinutes(15, arrivalTime),
              latitude: routeData.startLocation[0],
              longitude: routeData.startLocation[1],
              propertyId: null
            })
            updatedRoute.customStartAddress = locationAddress
            updatedRoute.customStartTime = arrivalTime
          }

          if (isCustomEndInRoute(routeData)) {
            const arrivalTime = moment(thisTime)
              .add(
                returnedRoute.routes[0].legs[waypoints.length].duration.value +
                  1800,
                'seconds'
              )
              .utc()
            const locationAddress =
              returnedRoute.routes[0].legs[waypoints.length].end_address
            updatedCustomStops.push({
              Address: `Custom End Location: ${locationAddress}`,
              arrivalTime: roundOffMinutes(15, arrivalTime),
              latitude: routeData.endLocation[0],
              longitude: routeData.endLocation[1],
              propertyId: null
            })
            updatedRoute.customEndTime = arrivalTime
            updatedRoute.customEndAddress = locationAddress
          }
        }
      }

      updatedRoute = {
        ...updatedRoute,
        calculatedArray: [...updatedCustomStops],
        markers: this.setMarkers(updatedCustomStops)
      }
      return updatedRoute
    },

    getFullAddress(home) {
      if (home) {
        const propertyObject = this.clientFavorites.find(
          property => property._id === home.propertyId
        )
        if (propertyObject) return propertyFullAddress(propertyObject)
      }
      return null
    },

    async getPropertyObject(propertyId) {
      let propertyObject = null
      if (!isEmpty(this.clientFavorites)) {
        propertyObject = this.clientFavorites.find(
          fav => fav.propertyId === propertyId
        )
      }
      if (!propertyObject) {
        propertyObject = (await getPropertyById(propertyId)).data
      }
      return propertyObject
    },

    updateAgentRouteEvents(selectedRoute, updatedRoute) {
      const routeList =
        this.profileStatus === 'client'
          ? [...this.clientRouteData]
          : [...this.agentRouteData]

      const updatedRouteList = routeList.map(route => {
        if (route._id === selectedRoute._id) {
          return updatedRoute
        }
        return route
      })

      this.profileStatus === 'client'
        ? this.setClientRouteData(updatedRouteList)
        : this.setAgentRouteData(updatedRouteList)
    },

    getCustomStartAddress(route) {
      return route.customStartAddress
        ? route.customStartAddress
        : this.startLocationAddress
    },

    getCustomEndAddress(route) {
      return route.customEndAddress
        ? route.customEndAddress
        : this.endLocationAddress
    },

    getCustomStartTime(route) {
      return route.customStartTime
        ? route.customStartTime
        : this.startLocationTime
    },

    getCustomEndTime(route) {
      return route.customEndTime ? route.customEndTime : this.endLocationTime
    },

    // Function to calculate route directions, marker array etc.
    // and update the route object in store and selected route in planner
    async calculateRouteHomeMarkerArray(routeData, planRoutePage = false) {
      let fullCalculatedArray = []
      let calculatedArray = []

      for (const stop of routeData.routeStops) {
        const propertyObject = await this.getPropertyObject(stop.propertyId)
        if (propertyObject) {
          fullCalculatedArray.push({
            ...propertyObject,
            ...stop,
            _id: propertyObject._id
          })
          if (isActiveRouteStop(stop)) {
            calculatedArray.push({
              Address: propertyFullAddress(propertyObject),
              ...stop,
              latitude: propertyObject.Latitude,
              longitude: propertyObject.Longitude,
              _id: propertyObject._id
            })
          }
        }
      }

      let updatedRoute = { ...routeData }
      if (!isEmpty(calculatedArray)) {
        updatedRoute = await this.getRouteDirections(
          calculatedArray,
          routeData
        )
      }

      updatedRoute = {
        ...updatedRoute,
        fullCalculatedArray: [...fullCalculatedArray]
      }

      if (!isDeclinedRoute(routeData) && !isNotScheduledType(routeData)) {
        updatedRoute = {
          ...updatedRoute,
          ...createCalendarTimeFrame(updatedRoute)
        }
      }

      this.updateAgentRouteEvents(routeData, updatedRoute)
      if (isCancelledRoute(updatedRoute)) {
        updatedRoute = {
          ...updatedRoute,
          markers: this.cancelledRouteMarkers(updatedRoute.fullCalculatedArray)
        }
      }
      if (planRoutePage) this.setSelectedRoute(updatedRoute)
      return updatedRoute
    },

    cancelledRouteMarkers(fromArray) {
      return fromArray.map(item => ({
        position: {
          lat: item.Latitude,
          lng: item.Longitude
        },
        address: propertyFullAddress(item)
      }))
    },

    setMarkers(fromArray) {
      return fromArray.map(item => ({
        position: {
          lat: item.latitude,
          lng: item.longitude
        },
        address: item.Address
      }))
    },

    routesUpdateAfterActions(response, clientData, routeList) {
      response = {
        ...response,
        clientId: clientData._id,
        clientName: clientData.name
      }
      this.setAgentRouteData(updateRouteDataOnResponse(response, routeList))
      return response
    },

    updateRhtAfterRequestShowing(data) {
      const routeStopIds = data.routeStops.map(item => item.propertyId)
      const updatedRht = this.rhtValue.data.filter(
        home => !routeStopIds.includes(home._id)
      )
      this.setRht({ ...this.rhtValue, data: updatedRht })
    },

    updateRhtAfterCancelRoute(data) {
      let cancelledRouteProperties = []
      data.routeStops.forEach(stop => {
        const clientFav = getClientFavFromPropertyId(
          this.clientFavorites,
          stop.propertyId
        )
        if (clientFav) cancelledRouteProperties.push(clientFav)
      })
      let updatedRht = [...this.rhtValue.data]
      if (!isEmpty(cancelledRouteProperties)) {
        updatedRht = [...cancelledRouteProperties, ...updatedRht]
      }
      this.setRht({ ...this.rhtValue, data: updatedRht })
    },

    // Function to update agent route data after confirmed/scheduled cancel
    updateAgentRoutesOnCancel(data) {
      let updatedAgentRouteData = [...this.agentRouteData]
      updatedAgentRouteData = updatedAgentRouteData.map(route =>
        route._id === data._id ? data : route
      )
      this.setAgentRouteData(updatedAgentRouteData)
    },

    // Function to update client data after cancel/delete if exists
    updateClientDataOnCancel(clientData, data, isSaved) {
      if (!isEmpty(clientData)) {
        let updatedClientRoutes = [ ...clientData.routes ]
        if (isSaved) {
          updatedClientRoutes = updatedClientRoutes.filter(
            route => route._id !== data._id
          )
        } else {
          updatedClientRoutes = updatedClientRoutes.map(route =>
            route._id === data._id ? data : route
          )
        }
        this.setClientData({
          ...this.clientData,
          routes: updatedClientRoutes
        })
      }
    }
  }
}
